<!-- Component to add code editing like behaviour to Field Calculation formulae/expressions etc. -->
<template>
  <!-- Formula -->
  <b-form-group :id="id" class="properties_group formulaEditor">
    <div class="row">
      <div class="col">
        <legend class="bv-no-focus-ring col-form-label pt-0">
          {{ label || 'Formula' }}<span class="text-danger">&nbsp;*</span>
        </legend>
      </div>
      <div class="fixed mr-2">
        <!-- Verify Formula button -->
        <b-button id="verifyFormulaButton" class="pt-1 pb-1 mr-0 mb-1 mw60" size="sm" variant="outline-success"
          v-show="constructFieldDefinition" :disabled="!formula || formulaVerify.active" @click="onVerifyFormula()">
          Verify
        </b-button>
      </div>
      <div class="fixed mr-3">
        <!-- Clear Formula button -->
        <b-button id="clearFormulaButton" class="pt-1 pb-1 mr-2 mb-1 mw60" size="sm" variant="outline-warning"
          :disabled="!formula || formulaVerify.active" @click="onClearFormula()">
          Clear
        </b-button>
      </div>
    </div>
    <code-editor id="codeEditor" ref="codeEditor" :document="formula" :language="language"
      @doc-changed="onFormulaChanged" :placeholder="label || 'Formula'" />
    <transition name="fade">
      <content-spinner :show="formulaVerify.active" text="Verifying formula. Please wait ..."
        class="border rounded mt-1" />
    </transition>
    <!-- Verify Formula output - Error, Warning and Info messages. -->
    <div v-if="!!formulaVerify.error" class="alert alert-danger mt-1 mb-1 pt-2 pb-2">
      <div class="row">
        <div class="fixed"><strong>Error!</strong></div>
        <div class="col">{{ formulaVerify.error }}</div>
        <div class="fixed">
          <b-button id="askVerifyFormulaError" class="bg-light" size="sm" variant="outline-info"
            v-if="assistantAvailable()" @click="askAssistantError(formulaVerify.error)">
            Ask Assistant
          </b-button>
        </div>
      </div>
    </div>
    <div v-if="!!formulaVerify.warning" class="alert alert-warning mt-1 mb-1 pt-2 pb-2">
      <div class="row">
        <div class="fixed"><strong>Warning!</strong></div>
        <div class="col">{{ formulaVerify.warning }}</div>
      </div>
    </div>
    <div v-if="!!formulaVerify.message" class="alert alert-success mt-1 mb-1 pt-2 pb-2">
      <div class="row">
        <div class="fixed"><strong>Success!</strong></div>
        <div class="col">{{ formulaVerify.message }}</div>
      </div>
    </div>
  </b-form-group>
</template>

<script>

import ContentSpinner from '@/core/components/ContentSpinner.vue';
import CodeEditor from '../../components/CodeEditor.vue';
import * as apiUtils from '@/core/utils/apiUtils';
import { timeout } from 'd3';

export default {

  name: 'CalculationFormulaCodeEditor',

  components: {
    ContentSpinner,
    CodeEditor,
  },

  props: {
    id: { type: String, },
    label: { type: String, },
    formula: { type: String, required: true, },
    language: { type: String, default: 'javascript', },
    /** Function to return warning message about calculation from parent component to display in place of Verifying formula.  */
    calculationWarningOnVerify:{ type: Function,},
    /**
     * Function to return the Atlas Assistant.
     * - NB Check that it is available before interacting.
     */
    atlasAssistant: { type: Function, },
    /**
     * Function to provide the Field defiition with the latest (edited) version of the calculation.
     * - Used to Verify the formula.
     */
    constructFieldDefinition: { type: Function, },
  },

  data() {
    return {
      /** Formula verification properties. */
      formulaVerify: {
        active: false,
        message: '',
        warning: '',
        error: '',
      },
    };
  },

  watch: {
  },

  methods: {

    // Code Editor utilities.
    getCodeEditor() {
      return this.$refs.codeEditor;
    },
    onFormulaChanged(formula) {
      this.setFormulaVerify();
      this.$emit('formula-changed', formula);
    },
    replaceTextSelection(text, cursorOffset = 0) {
      this.getCodeEditor().replaceTextSelection(text, cursorOffset);
    },
    setFormulaVerify(data) {
      this.$set(this, 'formulaVerify', data || {});
    },
    async onVerifyFormula() {
      this.setFormulaVerify();
      if (!this.constructFieldDefinition) return;
      if (this.calculationWarningOnVerify){
        const warning = this.calculationWarningOnVerify();
        if (warning){
          this.formulaVerify.warning = warning;
          return;
        }
      }
      this.setFormulaVerify({ active: true });
      const field = this.constructFieldDefinition();
      const response = await apiUtils.verifyFieldCalculation(field);
      timeout(() => {
        this.setFormulaVerify(response.body);
      }, 500);
    },
    onClearFormula() {
      this.getCodeEditor().setEditorDoc('');
      this.setFormulaVerify();
    },

    // Verify Messages - cna be set by parent component.
    setMessage(msg){
      this.setFormulaVerify({active: this.formulaVerify?.active});
      this.$set(this.formulaVerify, 'message', msg);
    },
    setWarning(msg){
      this.setFormulaVerify({active: this.formulaVerify?.active});
      this.$set(this.formulaVerify, 'warning', msg);
    },
    setError(msg){
      this.setFormulaVerify({active: this.formulaVerify?.active});
      this.$set(this.formulaVerify, 'error', msg);
    },

    // Atlas Assistant.
    assistantAvailable() {
      return this.atlasAssistant && this.atlasAssistant()?.assistantAvailable;
    },
    askAssistantError(error) {
      if (!this.assistantAvailable()) return;
      this.atlasAssistant().askFormulaError(this.formula, error);
      this.setFormulaVerify();
    },

  },

  mounted() {
  },

  beforeDestroy() {
  },

};
</script>

<style lang="scss">
button.mw60 {
  min-width: 60px;
}
</style>